import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from 'lib/utils';
import {
  MODAL_CONTENT_MOTION_CLASS,
  MODAL_GUTTER_WIDTH_CLASS,
  MODAL_OVERLAY_CLASS,
  MODAL_SURFACE_CLASS,
} from './modal-styles';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      MODAL_OVERLAY_CLASS,
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    closeDisabled?: boolean;
    overlayClassName?: string;
  }
>((
  {
    className,
    children,
    hideCloseButton = false,
    closeDisabled = false,
    overlayClassName,
    onOpenAutoFocus,
    onCloseAutoFocus,
    ...props
  },
  ref,
) => {
  const returnFocusTargetRef = React.useRef<HTMLElement | null>(null);
  const isCohortDialog = typeof className === 'string'
    && className.split(/\s+/).includes('cohort-dialog');

  const handleOpenAutoFocus = (event: Event) => {
    const activeElement = document.activeElement;
    returnFocusTargetRef.current =
      activeElement instanceof HTMLElement && activeElement !== document.body
        ? activeElement
        : null;
    onOpenAutoFocus?.(event);
  };

  const handleCloseAutoFocus = (event: Event) => {
    onCloseAutoFocus?.(event);

    if (event.defaultPrevented) {
      return;
    }

    const returnFocusTarget = returnFocusTargetRef.current;
    if (!returnFocusTarget) {
      return;
    }

    window.setTimeout(() => {
      if (returnFocusTarget.isConnected) {
        returnFocusTarget.focus({ preventScroll: true });
      }
    }, 0);
  };

  return (
    <DialogPortal>
      <DialogOverlay
        className={cn(isCohortDialog && 'cohort-dialog-overlay', overlayClassName)}
        data-testid={isCohortDialog ? 'cohort-dialog-overlay' : undefined}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100dvh-1rem)] max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto p-4 sm:p-8',
          MODAL_GUTTER_WIDTH_CLASS,
          MODAL_SURFACE_CLASS,
          MODAL_CONTENT_MOTION_CLASS,
          className
        )}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        {...props}
      >
        {children}
        {!hideCloseButton ? (
          <DialogPrimitive.Close
            disabled={closeDisabled}
            className={cn(
              "absolute right-4 top-4 rounded-full p-2 text-muted-foreground transition hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              closeDisabled ? "cursor-not-allowed opacity-50 hover:bg-transparent focus-visible:ring-0" : "",
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-2xl font-semibold text-foreground', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose
};
